import React,{ useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

// ── Inline styles & design tokens ──────────────────────────────────────────
const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');`;

const C = {
  sand: "#EDEBE3", sandDark: "#D2CDB1", cream: "#F9F9F8",
  ocean: "#53838D", oceanLight: "#71A2AC", oceanPale: "#EBEFEF", oceanDark: "#37565D",
  gold: "#F1D22C", goldLight: "#F4DA52", goldPale: "#F7F5E8", goldDark: "#BFA30D",
  charcoal: "#13191A", slate: "#596A6E", mist: "#96A3A5",
  white: "#FFFFFF", offwhite: "#FAFAF8",
  danger: "#C0392B", success: "#27AE60",
  // Deprecated brand roles kept as aliases so existing call sites keep working
  // without a full rewrite. coral (accent) -> gold, forest (dark section bg) -> oceanDark.
  coral: "#F1D22C", coralLight: "#F4DA52", coralPale: "#F7F5E8",
  forest: "#37565D", forestLight: "#53838D",
};

const API = "https://mdgholidaydreams.com/api";

// ── Utility helpers ──────────────────────────────────────────────────────────
const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
const stars = (n) => "★".repeat(Math.round(n)) + "☆".repeat(5 - Math.round(n));
const slugify = (s) => s.toLowerCase().replace(/\s+/g, "-");
const genId = () => Math.floor(Math.random() * 90000) + 10000;

// ── Global CSS ───────────────────────────────────────────────────────────────
const GlobalStyle = () => (
  <style>{`
    ${FONTS}
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'DM Sans', sans-serif; background: ${C.offwhite}; color: ${C.charcoal}; }
    h1,h2,h3,h4,h5 { font-family: 'Playfair Display', Georgia, serif; }
    a { color: inherit; text-decoration: none; }
    button { cursor: pointer; font-family: 'DM Sans', sans-serif; }
    input, textarea, select { font-family: 'DM Sans', sans-serif; }
    img { object-fit: cover; }
    .scroll-lock { overflow: hidden; }
    ::-webkit-scrollbar { width: 6px; height: 6px; }
    ::-webkit-scrollbar-track { background: ${C.sand}; }
    ::-webkit-scrollbar-thumb { background: ${C.mist}; border-radius: 3px; }
    @keyframes fadeIn { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:none; } }
    @keyframes slideIn { from { transform:translateX(-100%); } to { transform:translateX(0); } }
    @keyframes pulse { 0%,100%{opacity:1;} 50%{opacity:0.5;} }
    .fade-in { animation: fadeIn 0.4s ease forwards; }
    .btn-primary { background: ${C.ocean}; color: #fff; border: none; padding: 12px 28px; border-radius: 4px; font-size: 15px; font-weight: 500; letter-spacing: 0.3px; transition: all 0.2s; }
    .btn-primary:hover { background: ${C.oceanLight}; transform: translateY(-1px); }
    .btn-outline { background: transparent; color: ${C.ocean}; border: 1.5px solid ${C.ocean}; padding: 11px 26px; border-radius: 4px; font-size: 15px; font-weight: 500; transition: all 0.2s; }
    .btn-outline:hover { background: ${C.ocean}; color: #fff; }
    .btn-coral { background: ${C.goldDark}; color: #fff; border: none; padding: 12px 28px; border-radius: 4px; font-size: 15px; font-weight: 500; transition: all 0.2s; }
    .btn-coral:hover { background: ${C.gold}; transform: translateY(-1px); }
    .badge { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 500; }
    .badge-ocean { background: ${C.oceanPale}; color: ${C.ocean}; }
    .badge-coral { background: ${C.coralPale}; color: ${C.coral}; }
    .badge-gold { background: ${C.goldPale}; color: ${C.gold}; }
    .badge-success { background: #e8f8ee; color: ${C.success}; }
    .badge-danger { background: #fde8e6; color: ${C.danger}; }
    .card-hover { transition: transform 0.25s, box-shadow 0.25s; }
    .card-hover:hover { transform: translateY(-4px); box-shadow: 0 16px 40px rgba(26,74,92,0.12); }
    input:focus, textarea:focus, select:focus { outline: 2px solid ${C.oceanLight}; outline-offset: 1px; }
  `}</style>
);

// ── Nav ──────────────────────────────────────────────────────────────────────
function Navbar({ page, setPage, isAdmin }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const navBg = scrolled || page !== "home" ? `rgba(250,247,240,0.97)` : "transparent";
  const textColor = scrolled || page !== "home" ? C.charcoal : C.white;
  const logoColor = scrolled || page !== "home" ? C.ocean : C.white;

  const links = [
    { id: "destinations", label: "Destinations" },
    { id: "packages", label: "Packages" },
    { id: "excursions", label: "Activities" },
    { id: "stays", label: "Stay" },
    { id: "contact", label: "Contact" },
  ];

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      background: navBg, backdropFilter: scrolled ? "blur(12px)" : "none",
      borderBottom: scrolled ? `1px solid ${C.sand}` : "none",
      transition: "all 0.3s", padding: "0 5%",
    }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 70 }}>
        <button onClick={() => setPage("home")} style={{ background: "none", border: "none", display: "flex", flexDirection: "column", lineHeight: 1 }}>
          <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 700, color: logoColor, letterSpacing: "-0.5px" }}>M H D</span>
          <span style={{ fontSize: 8, letterSpacing: 3, color: logoColor, opacity: 0.75, marginTop: 2 }}>MADAGASCAR HOLIDAY DREAMS</span>
        </button>
        <div style={{ display: "flex", gap: 32, alignItems: "center" }}>
          {links.map(l => (
            <button key={l.id} onClick={() => setPage(l.id)} style={{
              background: "none", border: "none", fontSize: 14, fontWeight: 500,
              color: page === l.id ? C.coral : textColor,
              letterSpacing: 0.3, transition: "color 0.2s",
              borderBottom: page === l.id ? `2px solid ${C.coral}` : "2px solid transparent",
              paddingBottom: 2,
            }}>{l.label}</button>
          ))}
         { /*  <Link to={isAdmin ? "/dashboard" : "/login"} style={{ textDecoration: "none", color: "inherit" }}>
                {isAdmin ? "Dashboard" : "Admin"}
              </Link> */}
        </div>
      </div>
    </nav>
  );
}

// ── WhatsApp Button ──────────────────────────────────────────────────────────
function WhatsAppButton() {
  return (
    <a href="https://wa.me/255700000000?text=Hello!%20I%20am%20interested%20in%20booking%20a%20trip."
      target="_blank" rel="noopener noreferrer"
      style={{
        position: "fixed", bottom: 28, right: 28, zIndex: 999,
        width: 56, height: 56, borderRadius: "50%",
        background: "#25D366", display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 4px 20px rgba(37,211,102,0.4)", transition: "transform 0.2s",
      }}
      onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"}
      onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
      title="Chat on WhatsApp"
    >
      <svg viewBox="0 0 24 24" fill="white" width="28" height="28">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
        <path d="M12.004 2C6.477 2 2 6.477 2 12.004c0 1.768.461 3.493 1.34 5.014L2 22l5.1-1.322A10.003 10.003 0 0012.004 22C17.523 22 22 17.523 22 12.004 22 6.477 17.523 2 12.004 2zm0 18.321a8.312 8.312 0 01-4.235-1.162l-.304-.18-3.146.815.836-3.062-.198-.317A8.297 8.297 0 013.692 12c0-4.591 3.735-8.326 8.312-8.326 4.585 0 8.314 3.735 8.314 8.326 0 4.586-3.729 8.321-8.314 8.321z"/>
      </svg>
    </a>
  );
}

// ── Hero ─────────────────────────────────────────────────────────────────────
function Hero({ setPage }) {
  const [slides, setSlides] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [current, setCurrent] = useState(0);
  const [prev, setPrev] = useState(null);
  const [animating, setAnimating] = useState(false);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    axios.get(`${API}/cities?featured_on_homepage=1&active=1`)
      .then(res => {
        const mapped = res.data.map(c => ({
          img: c.image_url,
          location: c.name,
          dest: c.name,
          caption: c.caption,
          tag: (c.name || "").toUpperCase(),
          country: c.country_name,
          color: c.color || C.ocean,
        }));
        setSlides(mapped);
      })
      .catch(console.error)
      .finally(() => setLoaded(true));
  }, []);

  const DURATION = 6000;

  const goTo = useCallback((idx) => {
    if (animating || idx === current) return;
    setPrev(current);
    setAnimating(true);
    setProgress(0);
    setTimeout(() => { setPrev(null); setAnimating(false); }, 1100);
    setCurrent(idx);
  }, [animating, current]);

  const next = useCallback(() => goTo((current + 1) % slides.length), [current, goTo, slides.length]);
  const prev_ = useCallback(() => goTo((current - 1 + slides.length) % slides.length), [current, goTo, slides.length]);

  useEffect(() => {
    if (paused || slides.length === 0) return;
    setProgress(0);
    const start = Date.now();
    const raf = setInterval(() => setProgress(Math.min(((Date.now() - start) / DURATION) * 100, 100)), 50);
    const t = setTimeout(next, DURATION);
    return () => { clearInterval(raf); clearTimeout(t); };
  }, [current, paused, next, slides.length]);

  if (!loaded) {
    return <div style={{ height: "100vh", minHeight: 640, background: C.charcoal }} />;
  }

  if (slides.length === 0) {
    return (
      <div style={{ height: "100vh", minHeight: 640, background: C.ocean, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p style={{ color: "rgba(255,255,255,0.7)" }}>No featured destinations yet.</p>
      </div>
    );
  }

  const sl = slides[current];

  return (
    <div style={{ position: "relative", height: "100vh", minHeight: 640, overflow: "hidden" }}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <style>{`
        @keyframes kenburns { from { transform: scale(1.08); } to { transform: scale(1); } }
        @keyframes slideUp { from { opacity:0; transform:translateY(22px); } to { opacity:1; transform:none; } }
        @keyframes slideUpFast { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:none; } }
      `}</style>

      {slides.map((s, i) => (
        <div key={i} style={{ position: "absolute", inset: 0, opacity: i === current ? 1 : 0, transition: i === current ? "opacity 1.1s cubic-bezier(0.4,0,0.2,1)" : "opacity 0.8s ease", zIndex: i === current ? 2 : i === prev ? 1 : 0 }}>
          <div style={{ position: "absolute", inset: 0, animation: i === current ? "kenburns 8s ease-out forwards" : "none" }}>
            <img src={s.img} alt={s.location} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          </div>
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.05) 35%, rgba(0,0,0,0.45) 70%, rgba(0,0,0,0.78) 100%)" }} />
          <div style={{ position: "absolute", inset: 0, background: `radial-gradient(ellipse at 80% 50%, ${s.color}22 0%, transparent 60%)` }} />
        </div>
      ))}

      <div style={{ position: "relative", zIndex: 10, height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: "0 6% 120px" }}>
        <div key={`meta-${current}`} style={{ marginBottom: 20, animation: "slideUpFast 0.6s 0.1s ease both" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.25)", color: "#fff", padding: "7px 18px", borderRadius: 30, fontSize: 12, fontWeight: 600, letterSpacing: 2 }}>
            <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: sl.color }} />
            {sl.tag} · {sl.country}
          </span>
        </div>
        <div key={`loc-${current}`} style={{ animation: "slideUp 0.7s 0.15s ease both" }}>
          <h1 style={{ color: "#fff", lineHeight: 1.0, marginBottom: 16, fontSize: "clamp(48px, 7.5vw, 96px)", textShadow: "0 4px 32px rgba(0,0,0,0.4)", fontStyle: "italic" }}>{sl.location}</h1>
        </div>
        <div key={`cap-${current}`} style={{ animation: "slideUp 0.7s 0.25s ease both", marginBottom: 36 }}>
          <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "clamp(15px,1.8vw,19px)", maxWidth: 500, lineHeight: 1.65, fontWeight: 300, letterSpacing: 0.3 }}>{sl.caption}</p>
        </div>
        <div key={`btns-${current}`} style={{ display: "flex", gap: 14, flexWrap: "wrap", animation: "slideUp 0.6s 0.35s ease both" }}>
          <button className="btn-primary" onClick={() => setPage("packages")} style={{ fontSize: 15, padding: "13px 32px", background: sl.color, border: "none", boxShadow: `0 8px 28px ${sl.color}55` }}>
            Explore {sl.dest}
          </button>
          <button onClick={() => setPage("contact")} style={{ background: "rgba(255,255,255,0.12)", backdropFilter: "blur(10px)", color: "#fff", border: "1.5px solid rgba(255,255,255,0.45)", padding: "12px 28px", borderRadius: 4, fontSize: 15, fontWeight: 500 }}>
            Plan My Trip
          </button>
        </div>
      </div>

      {/* Thumbnail strip */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 20, display: "flex", height: 80 }}>
        {slides.map((s, i) => (
          <button key={i} onClick={() => goTo(i)} style={{ flex: 1, position: "relative", overflow: "hidden", border: "none", cursor: "pointer", padding: 0, outline: "none", borderTop: i === current ? `3px solid ${s.color}` : "3px solid rgba(255,255,255,0.15)", transition: "border-color 0.3s" }}>
            <img src={s.img} alt={s.location} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", filter: i === current ? "brightness(0.85)" : "brightness(0.45) saturate(0.6)", transition: "filter 0.4s" }} />
            <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.3)" }} />
            <div style={{ position: "relative", zIndex: 1, padding: "0 12px", display: "flex", flexDirection: "column", justifyContent: "center", height: "100%" }}>
              <div style={{ fontSize: 9, letterSpacing: 2, color: i === current ? s.color : "rgba(255,255,255,0.5)", fontWeight: 700, marginBottom: 3 }}>{s.tag}</div>
              <div style={{ fontSize: 12, color: i === current ? "#fff" : "rgba(255,255,255,0.6)", fontWeight: 600, lineHeight: 1.2 }}>{s.location}</div>
            </div>
            {i === current && !paused && (
              <div style={{ position: "absolute", bottom: 0, left: 0, height: 3, background: s.color, width: `${progress}%`, transition: "width 0.05s linear" }} />
            )}
          </button>
        ))}
      </div>

      {[{ label: "←", action: prev_, pos: { left: 24 } }, { label: "→", action: next, pos: { right: 24 } }].map(({ label, action, pos }) => (
        <button key={label} onClick={action} style={{ position: "absolute", top: "50%", transform: "translateY(-50%)", zIndex: 15, ...pos, width: 48, height: 48, borderRadius: "50%", background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", fontSize: 20, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.2s" }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.25)"; e.currentTarget.style.transform = "translateY(-50%) scale(1.1)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.12)"; e.currentTarget.style.transform = "translateY(-50%) scale(1)"; }}
        >{label}</button>
      ))}

      <div style={{ position: "absolute", top: 90, right: "6%", zIndex: 15, textAlign: "right" }}>
        <span style={{ color: "#fff", fontSize: 28, fontWeight: 700, fontFamily: "'Playfair Display',serif" }}>0{current + 1}</span>
        <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 14 }}> / 0{slides.length}</span>
      </div>

      <div style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 15, display: "flex", justifyContent: "center", paddingTop: 92, pointerEvents: "none" }}>
        <div style={{ fontSize: 11, letterSpacing: 5, color: "rgba(255,255,255,0.55)", fontWeight: 500 }}>EAST AFRICA & INDIAN OCEAN</div>
      </div>
    </div>
  );
}

// ── Stats Bar ────────────────────────────────────────────────────────────────
function StatsBar() {
  const stats = [
    { n: "1,200+", label: "Happy Travelers" },
    { n: "15+", label: "Years Experience" },
    { n: "3", label: "Island Destinations" },
    { n: "4.9★", label: "Average Rating" },
  ];
  return (
    <div style={{ background: C.ocean, padding: "32px 5%" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20, textAlign: "center" }}>
        {stats.map(s => (
          <div key={s.n}>
            <div style={{ fontSize: 36, fontWeight: 700, color: C.goldLight, fontFamily: "'Playfair Display',serif" }}>{s.n}</div>
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Product Card ─────────────────────────────────────────────────────────────
function ProductCard({ item, onClick }) {
  const parseTagString = (str) => {
    try {
      const parsed = JSON.parse(str);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // not JSON, fall through to comma-split
    }
    return str.split(",").map(t => t.trim()).filter(Boolean);
  };

  const tagList = Array.isArray(item.tags)
    ? item.tags
    : typeof item.tags === "string"
      ? parseTagString(item.tags)
      : item.amenities || [];

  return (
    <div className="card-hover fade-in" onClick={() => onClick(item)}
      style={{ background: "#fff", borderRadius: 12, overflow: "hidden", cursor: "pointer", border: `1px solid ${C.sand}` }}>
      <div style={{ position: "relative", paddingBottom: "62%", overflow: "hidden" }}>
        <img src={item.image} alt={item.title || item.name}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.4s" }}
          onMouseEnter={e => e.currentTarget.style.transform = "scale(1.05)"}
          onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
        />
        <div style={{ position: "absolute", top: 12, left: 12, display: "flex", gap: 6 }}>
          {!!item.featured && <span className="badge badge-gold">Featured</span>}
          {item.destination && <span className="badge badge-ocean">{item.destination}</span>}
        </div>
        {item.duration && (
          <div style={{ position: "absolute", bottom: 12, right: 12, background: "rgba(0,0,0,0.6)", color: "#fff", padding: "4px 10px", borderRadius: 20, fontSize: 12 }}>
            {item.duration}
          </div>
        )}
      </div>
      <div style={{ padding: "18px 20px 20px" }}>
        <div style={{ fontSize: 13, color: C.mist, marginBottom: 6 }}>{item.type?.toUpperCase()}</div>
        <h3 style={{ fontSize: 19, lineHeight: 1.25, marginBottom: 6, color: C.charcoal }}>{item.title || item.name}</h3>
        {item.subtitle && <p style={{ fontSize: 13, color: C.slate, marginBottom: 10 }}>{item.subtitle}</p>}
        {item.description && (
          <p style={{
            fontSize: 13, color: C.slate, lineHeight: 1.55, marginBottom: 12,
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>
            {item.description}
          </p>
        )}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
          {tagList.slice(0, 3).map(t => (
            <span key={t} className="badge badge-ocean" style={{ fontSize: 11 }}>{t}</span>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <span style={{ fontSize: 22, fontWeight: 700, color: C.ocean }}>{fmt(item.price)}</span>
            <span style={{ fontSize: 12, color: C.mist, marginLeft: 4 }}>{item.currency || "USD"}</span>
          </div>
          <div style={{ fontSize: 13, color: C.gold }}>
            {stars(item.rating)} <span style={{ color: C.mist }}>({item.reviews})</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Booking Modal ────────────────────────────────────────────────────────────
function BookingModal({ item, onClose, onBook }) {
  const hasItinerary = item.type === "excursion" && Array.isArray(item.itinerary) && item.itinerary.length > 1;
  const [itineraryDismissed, setItineraryDismissed] = useState(!hasItinerary);

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: "", email: "", phone: "", guests: 1, checkIn: "", checkOut: "", notes: "", payMethod: "card" });
  const [submitted, setSubmitted] = useState(false);

  const totalPrice = form.guests * item.price;
  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async () => {
    const booking = {
      id: "BK" + genId(),
      productId: item.id, productTitle: item.title || item.name,
      customer: form.name, email: form.email, phone: form.phone,
      guests: parseInt(form.guests), checkIn: form.checkIn, checkOut: form.checkOut || form.checkIn,
      total: totalPrice, status: "pending", paid: false, createdAt: new Date().toISOString().split("T")[0],
    };
    try {
      await axios.post(`${API}/bookings`, booking);
    } catch (err) {
      console.error("Failed to save booking", err);
    }
    onBook(booking);
    setSubmitted(true);
  };

  const isStep1Valid = form.name && form.email && form.phone;
  const isStep2Valid = form.checkIn && form.guests > 0;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#fff", borderRadius: 16, maxWidth: 560, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ padding: "24px 28px 20px", borderBottom: `1px solid ${C.sand}`, display: "flex", alignItems: "flex-start", gap: 16 }}>
          <img src={item.image} alt="" style={{ width: 72, height: 72, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: 19, marginBottom: 4 }}>{item.title || item.name}</h3>
            <div style={{ color: C.slate, fontSize: 14 }}>{item.destination} · {item.duration}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.ocean, marginTop: 6 }}>{fmt(item.price)} <span style={{ fontSize: 12, fontWeight: 400, color: C.mist }}>per person</span></div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 24, color: C.mist }}>×</button>
        </div>
        

        <div style={{ padding: "24px 28px 28px" }}>
          {submitted ? (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ width: 64, height: 64, borderRadius: "50%", background: C.success, color: "#fff", fontSize: 32, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>✓</div>
              <h3 style={{ fontSize: 24, marginBottom: 12, color: C.ocean }}>Booking Requested!</h3>
              <p style={{ color: C.slate, lineHeight: 1.7, marginBottom: 20 }}>
                Thank you, <strong>{form.name}</strong>! Your booking request has been received.<br />
                We'll send a confirmation to <strong>{form.email}</strong> within 24 hours.
              </p>
              <div style={{ background: C.oceanPale, borderRadius: 8, padding: 16, textAlign: "left", marginBottom: 24 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 13 }}>
                  <span style={{ color: C.mist }}>Check-in</span><span style={{ fontWeight: 500 }}>{form.checkIn}</span>
                  <span style={{ color: C.mist }}>Guests</span><span style={{ fontWeight: 500 }}>{form.guests}</span>
                  <span style={{ color: C.mist }}>Total</span><span style={{ fontWeight: 600, color: C.ocean }}>{fmt(totalPrice)}</span>
                </div>
              </div>
              <a href={`https://wa.me/255700000000?text=Hi! I just booked ${item.title || item.name}. My name is ${form.name}.`}
                target="_blank" rel="noopener noreferrer"
                style={{ display: "inline-block", background: "#25D366", color: "#fff", padding: "12px 24px", borderRadius: 8, fontSize: 14, fontWeight: 500 }}>
                Confirm via WhatsApp
              </a>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 8, marginBottom: 24, alignItems: "center" }}>
                {[1, 2, 3].map(s => (
                  <div key={s} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: step >= s ? C.ocean : C.sand, color: step >= s ? "#fff" : C.mist, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600 }}>{s < step ? "✓" : s}</div>
                    <span style={{ fontSize: 12, color: step === s ? C.ocean : C.mist, fontWeight: step === s ? 600 : 400 }}>{["Details", "Dates", "Payment"][s - 1]}</span>
                    {s < 3 && <div style={{ width: 24, height: 1, background: C.sand }} />}
                  </div>
                ))}
              </div>



              {step === 1 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <h4 style={{ fontSize: 16, marginBottom: 4 }}>Your Details</h4>
                  {[["name", "Full Name", "text"], ["email", "Email Address", "email"], ["phone", "Phone / WhatsApp", "tel"]].map(([field, label, type]) => (
                    <div key={field}>
                      <label style={{ fontSize: 13, color: C.slate, display: "block", marginBottom: 5 }}>{label}</label>
                      <input name={field} type={type} value={form[field]} onChange={handleChange} placeholder={label}
                        style={{ width: "100%", padding: "10px 14px", border: `1px solid ${C.sandDark}`, borderRadius: 6, fontSize: 14 }} />
                    </div>
                  ))}
                  <button className="btn-primary" disabled={!isStep1Valid} onClick={() => setStep(2)} style={{ marginTop: 8, opacity: isStep1Valid ? 1 : 0.5 }}>Continue →</button>
                </div>
              )}

              {step === 2 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <h4 style={{ fontSize: 16, marginBottom: 4 }}>Choose Dates & Guests</h4>
                  <div>
                    <label style={{ fontSize: 13, color: C.slate, display: "block", marginBottom: 5 }}>Check-in / Activity Date</label>
                    <input name="checkIn" type="date" value={form.checkIn} onChange={handleChange} min={new Date().toISOString().split("T")[0]}
                      style={{ width: "100%", padding: "10px 14px", border: `1px solid ${C.sandDark}`, borderRadius: 6, fontSize: 14 }} />
                  </div>
                  {item.type === "package" && (
                    <div>
                      <label style={{ fontSize: 13, color: C.slate, display: "block", marginBottom: 5 }}>Check-out Date</label>
                      <input name="checkOut" type="date" value={form.checkOut} onChange={handleChange}
                        style={{ width: "100%", padding: "10px 14px", border: `1px solid ${C.sandDark}`, borderRadius: 6, fontSize: 14 }} />
                    </div>
                  )}
                  <div>
                    <label style={{ fontSize: 13, color: C.slate, display: "block", marginBottom: 5 }}>Number of Guests</label>
                    <input name="guests" type="number" value={form.guests} onChange={handleChange} min={item.minGuests || 1} max={item.maxGuests || 20}
                      style={{ width: "100%", padding: "10px 14px", border: `1px solid ${C.sandDark}`, borderRadius: 6, fontSize: 14 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, color: C.slate, display: "block", marginBottom: 5 }}>Special Requests (optional)</label>
                    <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder="Dietary requirements, special occasions..."
                      style={{ width: "100%", padding: "10px 14px", border: `1px solid ${C.sandDark}`, borderRadius: 6, fontSize: 14, resize: "vertical" }} />
                  </div>
                  <div style={{ background: C.goldPale, borderRadius: 8, padding: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                      <span style={{ color: C.slate }}>{form.guests} × {fmt(item.price)}</span>
                      <span style={{ fontWeight: 700, color: C.ocean, fontSize: 18 }}>{fmt(totalPrice)}</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <button className="btn-outline" onClick={() => setStep(1)}>← Back</button>
                    <button className="btn-primary" disabled={!isStep2Valid} onClick={() => setStep(3)} style={{ flex: 1, opacity: isStep2Valid ? 1 : 0.5 }}>Continue →</button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <h4 style={{ fontSize: 16, marginBottom: 4 }}>Payment Method</h4>
                  {[["card", "Credit / Debit Card"], ["bank", "Bank Transfer"], ["whatsapp", "Pay via WhatsApp"]].map(([val, label]) => (
                    <label key={val} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", border: `1.5px solid ${form.payMethod === val ? C.ocean : C.sandDark}`, borderRadius: 8, cursor: "pointer", background: form.payMethod === val ? C.oceanPale : "#fff" }}>
                      <input type="radio" name="payMethod" value={val} checked={form.payMethod === val} onChange={handleChange} />
                      <span style={{ fontSize: 15 }}>{label}</span>
                    </label>
                  ))}
                  {form.payMethod === "card" && (
                    <div style={{ padding: 16, background: C.sand, borderRadius: 8, display: "flex", flexDirection: "column", gap: 10 }}>
                      <input placeholder="Card Number" style={{ width: "100%", padding: "10px 14px", border: `1px solid ${C.sandDark}`, borderRadius: 6, fontSize: 14 }} />
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        <input placeholder="MM/YY" style={{ padding: "10px 14px", border: `1px solid ${C.sandDark}`, borderRadius: 6, fontSize: 14 }} />
                        <input placeholder="CVV" style={{ padding: "10px 14px", border: `1px solid ${C.sandDark}`, borderRadius: 6, fontSize: 14 }} />
                      </div>
                      <p style={{ fontSize: 11, color: C.mist }}>Payments processed securely via Stripe</p>
                    </div>
                  )}
                  {form.payMethod === "bank" && (
                    <div style={{ background: C.oceanPale, borderRadius: 8, padding: 14, fontSize: 13, lineHeight: 1.8 }}>
                      <strong>Bank Transfer Details:</strong><br />
                      Bank: CRDB Bank Tanzania<br />
                      Account: 0150000123456<br />
                      Swift: CRDBTZTZ<br />
                      Ref: {form.name}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 10 }}>
                    <button className="btn-outline" onClick={() => setStep(2)}>← Back</button>
                    <button className="btn-coral" onClick={handleSubmit} style={{ flex: 1 }}>Confirm Booking · {fmt(totalPrice)}</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Home Page ────────────────────────────────────────────────────────────────
function HomePage({ setPage }) {
  const [selectedItem, setSelectedItem] = useState(null);
  const [featuredPackages, setFeaturedPackages] = useState([]);
  const [featuredExcursions, setFeaturedExcursions] = useState([]);
  const [destinationCities, setDestinationCities] = useState([]);
  const [destCounts, setDestCounts] = useState({});

  useEffect(() => {
    axios.get(`${API}/packages?featured=1&active=1`)
      .then(res => setFeaturedPackages(res.data))
      .catch(err => console.error(err));

    axios.get(`${API}/activitiesexcursions?featured=1&active=1`)
      .then(res => setFeaturedExcursions(res.data))
      .catch(err => console.error(err));

    axios.get(`${API}/cities?featured_destination=1&active=1`)
      .then(res => setDestinationCities(res.data.slice(0, 3)))
      .catch(err => console.error(err));

    // Fetch counts for destination cards, keyed by country_id
    Promise.all([
      axios.get(`${API}/packages?active=1`),
      axios.get(`${API}/activitiesexcursions?active=1`),
    ]).then(([pkgRes, excRes]) => {
      const pkgs = pkgRes.data;
      const excs = excRes.data;
      const counts = {};
      [...pkgs, ...excs].forEach(item => {
        if (item.country_id == null) return;
        counts[item.country_id] = (counts[item.country_id] || 0) + 1;
      });
      setDestCounts(counts);
    }).catch(err => console.error(err));
  }, []);

  return (
    <div>
      <Hero setPage={setPage} />
      <StatsBar />

      {/* Featured Packages */}
      <section style={{ padding: "80px 5%" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 48 }}>
            <div>
              <p style={{ color: C.coral, fontSize: 12, letterSpacing: 3, fontWeight: 600, marginBottom: 8 }}>CURATED JOURNEYS</p>
              <h2 style={{ fontSize: 40, lineHeight: 1.1, color: C.charcoal }}>Featured Packages</h2>
            </div>
            <button className="btn-outline" onClick={() => setPage("packages")}>View All Packages →</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 28 }}>
            {featuredPackages.map(p => (
              <ProductCard key={p.id} item={p} onClick={setSelectedItem} />
            ))}
          </div>
        </div>
      </section>

      {/* Destinations */}
      <section style={{ padding: "0 5% 80px", background: C.cream }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", paddingTop: 80 }}>
          <p style={{ color: C.coral, fontSize: 12, letterSpacing: 3, fontWeight: 600, marginBottom: 8, textAlign: "center" }}>OUR DESTINATIONS</p>
          <h2 style={{ fontSize: 40, textAlign: "center", marginBottom: 48 }}>Explore the Islands</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }}>
            {destinationCities.map(c => {
              const count = destCounts[c.country_id] || 0;
              return (
                <div key={c.id} className="card-hover" style={{ position: "relative", borderRadius: 12, overflow: "hidden", cursor: "pointer", minHeight: 340 }}
                  onClick={() => setPage("destinations")}>
                  <img src={c.image_url} alt={c.name} style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute", inset: 0 }} />
                  <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.7) 100%)" }} />
                  <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: 24 }}>
                    {c.country_name && <span style={{ fontSize: 11, color: C.goldLight, letterSpacing: 2, fontWeight: 500 }}>{c.country_name.toUpperCase()}</span>}
                    <h3 style={{ color: "#fff", fontSize: 28, margin: "4px 0 8px" }}>{c.name}</h3>
                    {c.caption && <p style={{ color: "rgba(255,255,255,0.75)", fontSize: 13 }}>{c.caption}</p>}
                    <span style={{ color: C.goldLight, fontSize: 12, marginTop: 8, display: "block" }}>
                      {count} {count === 1 ? "offering" : "offerings"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Excursions */}
      <section style={{ padding: "80px 5%" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 48 }}>
            <div>
              <p style={{ color: C.coral, fontSize: 12, letterSpacing: 3, fontWeight: 600, marginBottom: 8 }}>ACTIVITIES & EXCURSIONS</p>
              <h2 style={{ fontSize: 40, lineHeight: 1.1 }}>Unforgettable Experiences</h2>
            </div>
            <button className="btn-outline" onClick={() => setPage("excursions")}>View All Activities →</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 24 }}>
            {featuredExcursions.map(e => <ProductCard key={e.id} item={e} onClick={setSelectedItem} />)}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section style={{ padding: "80px 5%", background: C.ocean, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, opacity: 0.08, backgroundImage: "url('https://images.unsplash.com/photo-1590523277543-a94d2e4eb00b?w=800')", backgroundSize: "cover" }} />
        <div style={{ maxWidth: 800, margin: "0 auto", textAlign: "center", position: "relative" }}>
          <h2 style={{ color: "#fff", fontSize: 42, marginBottom: 20 }}>Ready for Your Dream Escape?</h2>
          <p style={{ color: "rgba(255,255,255,0.8)", fontSize: 17, lineHeight: 1.7, marginBottom: 36 }}>
            Let our local experts craft the perfect itinerary. From budget-friendly adventures to luxury retreats — we know these islands like our backyard.
          </p>
          <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
            <button className="btn-coral" onClick={() => setPage("packages")} style={{ fontSize: 16 }}>Browse Packages</button>
            <button onClick={() => setPage("contact")} style={{ background: "rgba(255,255,255,0.1)", color: "#fff", border: "1.5px solid rgba(255,255,255,0.4)", padding: "12px 28px", borderRadius: 4, fontSize: 16, fontWeight: 500 }}>
              Talk to an Expert
            </button>
          </div>
        </div>
      </section>

      {selectedItem && (
        <BookingModal item={selectedItem} onClose={() => setSelectedItem(null)}
          onBook={(b) => { setSelectedItem(null); alert("Booking confirmed! Ref: " + b.id); }} />
      )}
    </div>
  );
}

// ── Packages Page ────────────────────────────────────────────────────────────
function PackagesPage({ setBookings }) {
  const [packages, setPackages] = useState([]);
  const [excursions, setExcursions] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filter, setFilter] = useState("all");
  const [dest, setDest] = useState("all");

  useEffect(() => {
    axios.get(`${API}/packages?active=1`).then(res => setPackages(res.data)).catch(console.error);
    axios.get(`${API}/activitiesexcursions?active=1`).then(res => setExcursions(res.data)).catch(console.error);
  }, []);

  const allProducts = [...packages, ...excursions];
  const filtered = allProducts.filter(p =>
    (filter === "all" || p.category === filter || p.type === filter) &&
    (dest === "all" || p.destination === dest)
  );

  return (
    <div style={{ paddingTop: 90, minHeight: "100vh" }}>
      <div style={{ background: C.ocean, padding: "60px 5% 48px", textAlign: "center" }}>
        <p style={{ color: C.goldLight, fontSize: 12, letterSpacing: 3, marginBottom: 12 }}>ALL OFFERINGS</p>
        <h1 style={{ color: "#fff", fontSize: 48 }}>Packages & Activities</h1>
        <p style={{ color: "rgba(255,255,255,0.75)", marginTop: 12, fontSize: 16 }}>Curated travel experiences across the Indian Ocean islands</p>
      </div>

      <div style={{ background: "#fff", borderBottom: `1px solid ${C.sand}`, padding: "16px 5%", position: "sticky", top: 70, zIndex: 100 }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 13, color: C.mist, marginRight: 4 }}>Filter:</span>
          {[["all", "All"], ["package", "Packages"], ["excursion", "Excursions"]].map(([val, label]) => (
            <button key={val} onClick={() => setFilter(val)} style={{ padding: "6px 16px", borderRadius: 20, fontSize: 13, background: filter === val ? C.ocean : C.sand, color: filter === val ? "#fff" : C.charcoal, border: "none", fontWeight: 500 }}>{label}</button>
          ))}
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            {["all", "Zanzibar", "Seychelles", "Madagascar"].map(d => (
              <button key={d} onClick={() => setDest(d)} style={{ padding: "6px 14px", borderRadius: 20, fontSize: 13, background: dest === d ? C.coral : C.cream, color: dest === d ? "#fff" : C.charcoal, border: "none" }}>
                {d === "all" ? "All Destinations" : d}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "40px 5%" }}>
        <p style={{ color: C.mist, fontSize: 13, marginBottom: 24 }}>{filtered.length} results</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 28 }}>
          {filtered.map(p => <ProductCard key={`${p.type}-${p.id}`} item={p} onClick={setSelectedItem} />)}
        </div>
        {filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: 60, color: C.mist }}>
            <p style={{ marginTop: 16 }}>No results match your filters. Try adjusting them.</p>
          </div>
        )}
      </div>

      {selectedItem && (
        <BookingModal item={selectedItem} onClose={() => setSelectedItem(null)}
          onBook={(b) => { setBookings(prev => [b, ...prev]); setSelectedItem(null); }} />
      )}
    </div>
  );
}

// ── Destinations Page ─────────────────────────────────────────────────────────
function DestinationsPage({ setPage }) {
  const [countries, setCountries] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [packages, setPackages] = useState([]);
  const [excursions, setExcursions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/countries?active=1`)
      .then(res => {
        setCountries(res.data);
        if (res.data.length > 0) setActiveId(res.data[0].id);
      })
      .catch(console.error)
      .finally(() => setLoading(false));

    axios.get(`${API}/packages?active=1`).then(res => setPackages(res.data)).catch(console.error);
    axios.get(`${API}/activitiesexcursions?active=1`).then(res => setExcursions(res.data)).catch(console.error);
  }, []);

  const active = countries.find(c => c.id === activeId);
  const activePackages = active ? packages.filter(p => p.country_id === active.id) : [];
  const activeExcursions = active ? excursions.filter(e => e.country_id === active.id) : [];

  if (loading) {
    return <div style={{ paddingTop: 90, minHeight: "100vh" }} />;
  }

  if (!active) {
    return (
      <div style={{ paddingTop: 90, minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p style={{ color: C.mist }}>No destinations available right now.</p>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: 70, minHeight: "100vh" }}>
      <div style={{ display: "flex", borderBottom: `1px solid ${C.sand}`, background: "#fff", position: "sticky", top: 70, zIndex: 100 }}>
        {countries.map(c => (
          <button key={c.id} onClick={() => setActiveId(c.id)} style={{ flex: 1, padding: "18px 24px", border: "none", background: activeId === c.id ? C.ocean : "#fff", color: activeId === c.id ? "#fff" : C.charcoal, fontWeight: 600, fontSize: 16, cursor: "pointer", transition: "all 0.2s", borderBottom: activeId === c.id ? `3px solid ${C.coral}` : "3px solid transparent" }}>{c.name}</button>
        ))}
      </div>

      <div key={active.id} className="fade-in">
        <div style={{ position: "relative", height: 420, overflow: "hidden" }}>
          <img src={active.image_url} alt={active.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.6) 100%)" }} />
          <div style={{ position: "absolute", bottom: 48, left: "5%", right: "5%" }}>
            <h1 style={{ color: "#fff", fontSize: 52, lineHeight: 1.1 }}>{active.name}</h1>
            {active.tag && <p style={{ color: "rgba(255,255,255,0.85)", fontSize: 18, marginTop: 8 }}>{active.tag}</p>}
          </div>
        </div>

        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "56px 5%" }}>
          {active.note && (
            <div style={{ background: C.goldPale, border: `1px solid ${C.goldLight}`, borderRadius: 8, padding: "14px 20px", marginBottom: 36, display: "flex", gap: 10, alignItems: "center" }}>
              <span style={{ fontSize: 14 }}>{active.note}</span>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 60 }}>
            <div>
              {active.description && (
                <p style={{ fontSize: 17, lineHeight: 1.8, color: C.slate, marginBottom: 40 }}>{active.description}</p>
              )}
              {Array.isArray(active.offerings) && active.offerings.length > 0 && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 40 }}>
                  {active.offerings.map((o, i) => (
                    <span key={i} className="badge badge-ocean">{o}</span>
                  ))}
                </div>
              )}
              {activePackages.length > 0 && (
                <div style={{ marginBottom: 48 }}>
                  <h2 style={{ fontSize: 28, marginBottom: 24 }}>Packages in {active.name}</h2>
                  <div style={{ display: "grid", gap: 20 }}>
                    {activePackages.map(p => (
                      <div key={p.id} className="card-hover" style={{ display: "flex", gap: 20, background: "#fff", borderRadius: 12, overflow: "hidden", border: `1px solid ${C.sand}`, cursor: "pointer" }}>
                        <img src={p.image} alt={p.title} style={{ width: 160, height: 120, objectFit: "cover", flexShrink: 0 }} />
                        <div style={{ padding: "16px 20px 16px 0", flex: 1 }}>
                          <h3 style={{ fontSize: 18, marginBottom: 6 }}>{p.title}</h3>
                          <p style={{ color: C.slate, fontSize: 13, marginBottom: 8 }}>{p.duration}</p>
                          {p.description && (
                            <p style={{
                              color: C.slate, fontSize: 13, lineHeight: 1.55, marginBottom: 10,
                              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                            }}>
                              {p.description}
                            </p>
                          )}
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: 20, fontWeight: 700, color: C.ocean }}>{fmt(p.price)}</span>
                            <span style={{ fontSize: 13, color: C.gold }}>{stars(p.rating)} ({p.reviews})</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {activeExcursions.length > 0 && (
                <div>
                  <h2 style={{ fontSize: 28, marginBottom: 24 }}>Activities in {active.name}</h2>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    {activeExcursions.map(e => <ProductCard key={e.id} item={e} onClick={() => {}} />)}
                  </div>
                </div>
              )}
            </div>
            <div>
              <div style={{ background: "#fff", borderRadius: 12, border: `1px solid ${C.sand}`, padding: 24, position: "sticky", top: 130 }}>
                <h3 style={{ fontSize: 18, marginBottom: 20 }}>Quick Facts</h3>
                {[
                  ["Best Time", active.best_time],
                  ["Currency", active.currency],
                  ["Capital", active.capital],
                  ["Packages", activePackages.length],
                  ["Activities", activeExcursions.length],
                ].filter(([, val]) => val !== null && val !== undefined && val !== "").map(([label, val]) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${C.sand}`, fontSize: 14 }}>
                    <span style={{ color: C.mist }}>{label}</span>
                    <span style={{ fontWeight: 500 }}>{val}</span>
                  </div>
                ))}
                <button className="btn-primary" style={{ width: "100%", marginTop: 20 }} onClick={() => setPage("packages")}>Explore {active.name}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Excursions Page ──────────────────────────────────────────────────────────
function ExcursionsPage({ setBookings }) {
  const [excursions, setExcursions] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [dest, setDest] = useState("all");

  useEffect(() => {
    axios.get(`${API}/activitiesexcursions?active=1`).then(res => setExcursions(res.data)).catch(console.error);
  }, []);

  const filtered = excursions.filter(e => dest === "all" || e.destination === dest);

  return (
    <div style={{ paddingTop: 90, minHeight: "100vh" }}>
      <div style={{ background: C.forest, padding: "60px 5% 48px", textAlign: "center" }}>
        <p style={{ color: C.goldLight, fontSize: 12, letterSpacing: 3, marginBottom: 12 }}>DAY TRIPS & HALF DAYS</p>
        <h1 style={{ color: "#fff", fontSize: 48 }}>Activities & Excursions</h1>
        <p style={{ color: "rgba(255,255,255,0.75)", marginTop: 12, fontSize: 16 }}>Individual experiences you can add to any itinerary</p>
      </div>
      <div style={{ background: "#fff", borderBottom: `1px solid ${C.sand}`, padding: "16px 5%", position: "sticky", top: 70, zIndex: 100 }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", display: "flex", gap: 10 }}>
          {["all", "Zanzibar", "Seychelles", "Madagascar"].map(d => (
            <button key={d} onClick={() => setDest(d)} style={{ padding: "7px 18px", borderRadius: 20, fontSize: 13, border: "none", background: dest === d ? C.forest : C.sand, color: dest === d ? "#fff" : C.charcoal, fontWeight: 500 }}>
              {d === "all" ? "All Destinations" : d}
            </button>
          ))}
        </div>
      </div>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "40px 5%" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 28 }}>
          {filtered.map(e => <ProductCard key={e.id} item={e} onClick={setSelectedItem} />)}
        </div>
      </div>
      {selectedItem && (
        <BookingModal item={selectedItem} onClose={() => setSelectedItem(null)}
          onBook={(b) => { setBookings(prev => [b, ...prev]); setSelectedItem(null); }} />
      )}
    </div>
  );
}

// ── Stays Page ──────────────────────────────────────────────────────────────
function StaysPage({ setBookings }) {
  const [stays, setStays] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [dest, setDest] = useState("all");

  useEffect(() => {
    axios.get(`${API}/stays?active=1`).then(res => setStays(res.data)).catch(console.error);
  }, []);

  const filtered = stays.filter(h => dest === "all" || h.destination === dest);

  return (
    <div style={{ paddingTop: 90, minHeight: "100vh" }}>
      <div style={{ background: "#2C3E50", padding: "60px 5% 48px", textAlign: "center" }}>
        <p style={{ color: C.goldLight, fontSize: 12, letterSpacing: 3, marginBottom: 12 }}>PARTNER HOTELS</p>
        <h1 style={{ color: "#fff", fontSize: 48 }}>Where to Stay</h1>
        <p style={{ color: "rgba(255,255,255,0.75)", marginTop: 12, fontSize: 16 }}>Hand-picked hotels and lodges across our destinations</p>
      </div>
      <div style={{ background: "#fff", borderBottom: `1px solid ${C.sand}`, padding: "16px 5%", position: "sticky", top: 70, zIndex: 100 }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", display: "flex", gap: 10 }}>
          {["all", "Zanzibar", "Seychelles", "Madagascar"].map(d => (
            <button key={d} onClick={() => setDest(d)} style={{ padding: "7px 18px", borderRadius: 20, fontSize: 13, border: "none", background: dest === d ? "#2C3E50" : C.sand, color: dest === d ? "#fff" : C.charcoal, fontWeight: 500 }}>
              {d === "all" ? "All Destinations" : d}
            </button>
          ))}
        </div>
      </div>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "40px 5%" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 28 }}>
          {filtered.map(h => {
            const amenities = Array.isArray(h.amenities)
              ? h.amenities
              : typeof h.amenities === "string"
                ? (() => {
                    try {
                      const parsed = JSON.parse(h.amenities);
                      if (Array.isArray(parsed)) return parsed;
                    } catch {
                      // not JSON, fall through to comma-split
                    }
                    return h.amenities.split(",").map(a => a.trim()).filter(Boolean);
                  })()
                : [];
            return (
              <div key={h.id} className="card-hover" style={{ background: "#fff", borderRadius: 12, overflow: "hidden", border: `1px solid ${C.sand}`, cursor: "pointer" }}
                onClick={() => setSelectedItem({ ...h, title: h.name, type: "stay", duration: "Per night" })}>
                <div style={{ position: "relative", paddingBottom: "60%" }}>
                  <img src={h.image} alt={h.name} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                  <div style={{ position: "absolute", top: 12, left: 12 }}>
                    {!!h.featured && <span className="badge badge-gold">Featured</span>}
                  </div>
                  <div style={{ position: "absolute", top: 12, right: 12, background: "rgba(0,0,0,0.6)", color: "#fff", padding: "4px 10px", borderRadius: 20, fontSize: 13, fontWeight: 500 }}>
                    {"★".repeat(h.stars)}
                  </div>
                </div>
                <div style={{ padding: "18px 20px 20px" }}>
                  <div style={{ fontSize: 12, color: C.mist, marginBottom: 4 }}>{h.type} · {h.destination}</div>
                  <h3 style={{ fontSize: 18, marginBottom: 8 }}>{h.name}</h3>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                    {amenities.slice(0, 3).map(a => <span key={a} className="badge badge-ocean" style={{ fontSize: 11 }}>{a}</span>)}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div><span style={{ fontSize: 20, fontWeight: 700, color: C.ocean }}>{fmt(h.price)}</span><span style={{ fontSize: 12, color: C.mist }}> /night</span></div>
                    <span style={{ fontSize: 13, color: C.gold }}>{stars(h.rating)} ({h.reviews})</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {selectedItem && (
        <BookingModal item={selectedItem} onClose={() => setSelectedItem(null)}
          onBook={(b) => { setBookings(prev => [b, ...prev]); setSelectedItem(null); }} />
      )}
    </div>
  );
}

// ── Contact Page ─────────────────────────────────────────────────────────────
function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", dest: "", dates: "", guests: "", msg: "" });
  const [sent, setSent] = useState(false);
  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const inputStyle = { width: "100%", padding: "11px 14px", border: `1px solid ${C.sandDark}`, borderRadius: 6, fontSize: 14, background: "#fff" };

  const handleSend = async () => {
    if (!form.name || !form.email) return;
    try {
      await axios.post(`${API}/contact`, form);
    } catch (err) {
      console.error("Failed to send contact form", err);
    }
    setSent(true);
  };

  return (
    <div style={{ paddingTop: 90, minHeight: "100vh", background: C.cream }}>
      <div style={{ background: C.ocean, padding: "60px 5% 48px", textAlign: "center" }}>
        <h1 style={{ color: "#fff", fontSize: 48 }}>Contact Us</h1>
        <p style={{ color: "rgba(255,255,255,0.75)", marginTop: 12, fontSize: 16 }}>Our local experts are ready to craft your perfect journey</p>
      </div>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "60px 5%" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr", gap: 60 }}>
          <div>
            <h2 style={{ fontSize: 28, marginBottom: 24 }}>Get in Touch</h2>
            {[
              { icon: "O", label: "Office", val: "P.O. Box 1234, Stone Town\nZanzibar, Tanzania" },
              { icon: "P", label: "Phone", val: "+255 700 000 000" },
              { icon: "E", label: "Email", val: "hello@karibu-travel.co" },
              { icon: "W", label: "WhatsApp", val: "+255 700 000 000" },
              { icon: "H", label: "Office Hours", val: "Mon-Sat: 8am – 6pm EAT" },
            ].map(({ icon, label, val }) => (
              <div key={label} style={{ display: "flex", gap: 16, marginBottom: 24 }}>
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: C.oceanPale, color: C.ocean, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, flexShrink: 0 }}>{icon}</div>
                <div>
                  <div style={{ fontSize: 12, color: C.mist, fontWeight: 600, letterSpacing: 1 }}>{label.toUpperCase()}</div>
                  <div style={{ fontSize: 15, marginTop: 4, whiteSpace: "pre-line" }}>{val}</div>
                </div>
              </div>
            ))}
            <a href="https://wa.me/255700000000" target="_blank" rel="noopener noreferrer"
              style={{ display: "flex", alignItems: "center", gap: 12, background: "#25D366", color: "#fff", padding: "14px 24px", borderRadius: 8, fontWeight: 600, fontSize: 15, marginTop: 8 }}>
              Chat on WhatsApp Now
            </a>
          </div>
          <div style={{ background: "#fff", borderRadius: 16, padding: 36, border: `1px solid ${C.sand}` }}>
            {sent ? (
              <div style={{ textAlign: "center", padding: "30px 0" }}>
                <h3 style={{ fontSize: 24, marginBottom: 12 }}>Message Sent!</h3>
                <p style={{ color: C.slate, lineHeight: 1.7 }}>Thank you, <strong>{form.name}</strong>! Our team will respond within 12 hours.</p>
              </div>
            ) : (
              <>
                <h3 style={{ fontSize: 22, marginBottom: 24 }}>Plan My Trip</h3>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                  {[["name", "Full Name"], ["email", "Email"]].map(([f, l]) => (
                    <div key={f}><label style={{ fontSize: 12, color: C.mist, display: "block", marginBottom: 4 }}>{l}</label>
                      <input name={f} value={form[f]} onChange={handleChange} placeholder={l} style={inputStyle} /></div>
                  ))}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                  {[["phone", "Phone / WhatsApp"], ["guests", "Number of Guests"]].map(([f, l]) => (
                    <div key={f}><label style={{ fontSize: 12, color: C.mist, display: "block", marginBottom: 4 }}>{l}</label>
                      <input name={f} value={form[f]} onChange={handleChange} placeholder={l} style={inputStyle} /></div>
                  ))}
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 12, color: C.mist, display: "block", marginBottom: 4 }}>Preferred Destination</label>
                  <select name="dest" value={form.dest} onChange={handleChange} style={inputStyle}>
                    <option value="">Select destination...</option>
                    {["Zanzibar", "Seychelles", "Madagascar", "Multiple"].map(d => <option key={d}>{d}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 12, color: C.mist, display: "block", marginBottom: 4 }}>Travel Dates (approximate)</label>
                  <input name="dates" value={form.dates} onChange={handleChange} placeholder="e.g. July 2025 for 10 days" style={inputStyle} />
                </div>
                <div style={{ marginBottom: 24 }}>
                  <label style={{ fontSize: 12, color: C.mist, display: "block", marginBottom: 4 }}>Tell us about your dream trip</label>
                  <textarea name="msg" value={form.msg} onChange={handleChange} rows={4} placeholder="Interests, budget range, special requests..." style={{ ...inputStyle, resize: "vertical" }} />
                </div>
                <button className="btn-primary" style={{ width: "100%", fontSize: 16 }} onClick={handleSend}>Send Message</button>
              </>
            )}
          </div>
        </div>
      </div>
      <div style={{ background: C.sand, padding: "32px 5%", textAlign: "center" }}>
        <p style={{ fontSize: 13, color: C.slate }}>
          By contacting us, you agree to our{" "}
          <span style={{ color: C.ocean, cursor: "pointer", textDecoration: "underline" }}>Terms & Conditions</span> and{" "}
          <span style={{ color: C.ocean, cursor: "pointer", textDecoration: "underline" }}>Cancellation Policy</span>.
        </p>
      </div>
    </div>
  );
}

// ── Admin Login ──────────────────────────────────────────────────────────────
function AdminLogin({ onLogin }) {
  const [u, setU] = useState(""); const [p, setP] = useState(""); const [err, setErr] = useState(false);
  return (
    <div style={{ minHeight: "100vh", background: C.ocean, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: "48px 40px", width: "100%", maxWidth: 420, textAlign: "center" }}>
        <div style={{ fontSize: 14, color: C.ocean, letterSpacing: 3, fontWeight: 600, marginBottom: 8 }}>Madagascar Holidays Dreams.</div>
        <h2 style={{ fontSize: 28, marginBottom: 8 }}>Admin Portal</h2>
        <p style={{ color: C.mist, fontSize: 14, marginBottom: 32 }}>Sign in to manage your platform</p>
        {err && <div style={{ background: "#fde8e6", color: C.danger, padding: "10px 16px", borderRadius: 6, marginBottom: 16, fontSize: 13 }}>Invalid credentials</div>}
        <div style={{ marginBottom: 14 }}>
          <input value={u} onChange={e => setU(e.target.value)} placeholder="Username" style={{ width: "100%", padding: "12px 16px", border: `1px solid ${C.sandDark}`, borderRadius: 6, fontSize: 15, marginBottom: 12 }} />
          <input type="password" value={p} onChange={e => setP(e.target.value)} placeholder="Password" style={{ width: "100%", padding: "12px 16px", border: `1px solid ${C.sandDark}`, borderRadius: 6, fontSize: 15 }}
            onKeyDown={e => e.key === "Enter" && (u === "admin" && p === "admin123" ? onLogin() : setErr(true))} />
        </div>
        <button className="btn-primary" style={{ width: "100%", fontSize: 16, padding: "14px" }}
          onClick={() => u === "admin" && p === "admin123" ? onLogin() : setErr(true)}>
          Sign In
        </button>
      </div>
    </div>
  );
}

// ── Admin Dashboard ──────────────────────────────────────────────────────────
function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState("overview");
  const [packages, setPackages] = useState([]);
  const [excursions, setExcursions] = useState([]);
  const [stays, setStays] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    axios.get(`${API}/packages`).then(res => setPackages(res.data)).catch(console.error);
    axios.get(`${API}/activitiesexcursions`).then(res => setExcursions(res.data)).catch(console.error);
    axios.get(`${API}/stays`).then(res => setStays(res.data)).catch(console.error);
    axios.get(`${API}/bookings`).then(res => setBookings(res.data)).catch(console.error);
    axios.get(`${API}/reviews`).then(res => setReviews(res.data)).catch(console.error);
  }, []);

  const totalRevenue = bookings.filter(b => b.paid).reduce((s, b) => s + b.total, 0);
  const pendingRevenue = bookings.filter(b => !b.paid && b.status !== "cancelled").reduce((s, b) => s + b.total, 0);
  const confirmedBookings = bookings.filter(b => b.status === "confirmed").length;
  const pendingReviews = reviews.filter(r => !r.approved).length;

  const allProducts = [
    ...packages.map(p => ({ ...p, _type: "package" })),
    ...excursions.map(e => ({ ...e, _type: "excursion" })),
    ...stays.map(h => ({ ...h, _type: "stay", title: h.name })),
  ];

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "bookings", label: `Bookings (${bookings.length})` },
    { id: "products", label: `Products (${allProducts.length})` },
    { id: "reviews", label: `Reviews (${pendingReviews > 0 ? `${pendingReviews} pending` : reviews.length})` },
    { id: "add", label: "+ Add Product" },
  ];

  const toggleActive = async (product) => {
    const endpoint = `${API}/${product._type}s/${product.id}`;
    const updated = { ...product, active: !product.active };
    try {
      await axios.patch(endpoint, { active: updated.active });
      // TODO: refresh from API or update local state optimistically
      if (product._type === "package") setPackages(prev => prev.map(p => p.id === product.id ? updated : p));
      else if (product._type === "excursion") setExcursions(prev => prev.map(e => e.id === product.id ? updated : e));
      else setStays(prev => prev.map(h => h.id === product.id ? updated : h));
    } catch (err) {
      console.error("Failed to toggle active status", err);
    }
  };

  const approveReview = async (id) => {
    try {
      await axios.patch(`${API}/reviews/${id}`, { approved: true });
      setReviews(prev => prev.map(r => r.id === id ? { ...r, approved: true } : r));
    } catch (err) { console.error(err); }
  };

  const deleteReview = async (id) => {
    try {
      await axios.delete(`${API}/reviews/${id}`);
      setReviews(prev => prev.filter(r => r.id !== id));
    } catch (err) { console.error(err); }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#F0F2F5", display: "flex", flexDirection: "column" }}>
      <div style={{ background: C.charcoal, color: "#fff", padding: "0 32px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 60 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 700 }}>Madagascar Holidays Dreams</span>
          <span style={{ color: "rgba(255,255,255,0.4)" }}>|</span>
          <span style={{ fontSize: 14, color: "rgba(255,255,255,0.7)" }}>Admin Dashboard</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span style={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}>Logged in as Admin</span>
          <button onClick={onLogout} style={{ background: "rgba(255,255,255,0.1)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)", padding: "6px 16px", borderRadius: 4, fontSize: 13 }}>Logout</button>
        </div>
      </div>

      <div style={{ display: "flex", flex: 1 }}>
        <div style={{ width: 220, background: "#fff", borderRight: `1px solid ${C.sand}`, padding: "24px 0" }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{ display: "block", width: "100%", padding: "12px 24px", background: tab === t.id ? C.oceanPale : "none", color: tab === t.id ? C.ocean : C.charcoal, border: "none", textAlign: "left", fontWeight: tab === t.id ? 600 : 400, fontSize: 14, cursor: "pointer", borderLeft: tab === t.id ? `3px solid ${C.ocean}` : "3px solid transparent" }}>{t.label}</button>
          ))}
        </div>

        <div style={{ flex: 1, padding: "32px", overflowY: "auto" }}>
          {tab === "overview" && (
            <div className="fade-in">
              <h2 style={{ fontSize: 26, marginBottom: 28 }}>Business Overview</h2>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20, marginBottom: 36 }}>
                {[
                  { label: "Total Revenue", val: fmt(totalRevenue), sub: "From paid bookings", color: C.ocean },
                  { label: "Pending Revenue", val: fmt(pendingRevenue), sub: "Awaiting payment", color: C.gold },
                  { label: "Confirmed Bookings", val: confirmedBookings, sub: `of ${bookings.length} total`, color: C.forest },
                  { label: "Pending Reviews", val: pendingReviews, sub: "Need approval", color: pendingReviews > 0 ? C.coral : C.forest },
                ].map(k => (
                  <div key={k.label} style={{ background: "#fff", borderRadius: 12, padding: 24, border: `1px solid ${C.sand}` }}>
                    <div style={{ fontSize: 12, color: C.mist, marginBottom: 8, fontWeight: 600 }}>{k.label.toUpperCase()}</div>
                    <div style={{ fontSize: 32, fontWeight: 700, color: k.color }}>{k.val}</div>
                    <div style={{ fontSize: 12, color: C.mist, marginTop: 6 }}>{k.sub}</div>
                  </div>
                ))}
              </div>
              <div style={{ background: "#fff", borderRadius: 12, border: `1px solid ${C.sand}`, overflow: "hidden" }}>
                <div style={{ padding: "20px 24px", borderBottom: `1px solid ${C.sand}`, display: "flex", justifyContent: "space-between" }}>
                  <h3 style={{ fontSize: 18 }}>Recent Bookings</h3>
                  <button className="btn-outline" style={{ padding: "6px 14px", fontSize: 13 }} onClick={() => setTab("bookings")}>View All</button>
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead style={{ background: "#FAFAFA" }}>
                    <tr>{["Ref", "Customer", "Product", "Check-in", "Total", "Status"].map(h => (
                      <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 12, fontWeight: 600, color: C.mist, borderBottom: `1px solid ${C.sand}` }}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {bookings.slice(0, 5).map(b => (
                      <tr key={b.id} style={{ borderBottom: `1px solid ${C.sand}` }}>
                        <td style={{ padding: "12px 16px", fontSize: 13, color: C.ocean, fontWeight: 500 }}>{b.id}</td>
                        <td style={{ padding: "12px 16px", fontSize: 13 }}>{b.customer}</td>
                        <td style={{ padding: "12px 16px", fontSize: 13, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.productTitle}</td>
                        <td style={{ padding: "12px 16px", fontSize: 13 }}>{b.checkIn}</td>
                        <td style={{ padding: "12px 16px", fontSize: 13, fontWeight: 600 }}>{fmt(b.total)}</td>
                        <td style={{ padding: "12px 16px" }}>
                          <span className={`badge badge-${b.status === "confirmed" ? "success" : b.status === "cancelled" ? "danger" : "gold"}`}>{b.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "bookings" && (
            <div className="fade-in">
              <h2 style={{ fontSize: 26, marginBottom: 28 }}>All Bookings</h2>
              <div style={{ background: "#fff", borderRadius: 12, border: `1px solid ${C.sand}`, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead style={{ background: "#FAFAFA" }}>
                    <tr>{["Ref", "Customer", "Product", "Guests", "Check-in", "Total", "Paid", "Status"].map(h => (
                      <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, fontWeight: 600, color: C.mist, borderBottom: `1px solid ${C.sand}` }}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {bookings.map(b => (
                      <tr key={b.id} style={{ borderBottom: `1px solid ${C.sand}` }}>
                        <td style={{ padding: "12px 16px", fontSize: 13, color: C.ocean, fontWeight: 500 }}>{b.id}</td>
                        <td style={{ padding: "12px 16px", fontSize: 13 }}>
                          <div style={{ fontWeight: 500 }}>{b.customer}</div>
                          <div style={{ fontSize: 11, color: C.mist }}>{b.email}</div>
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: 13 }}>{b.productTitle}</td>
                        <td style={{ padding: "12px 16px", fontSize: 13 }}>{b.guests}</td>
                        <td style={{ padding: "12px 16px", fontSize: 13 }}>{b.checkIn}</td>
                        <td style={{ padding: "12px 16px", fontSize: 13, fontWeight: 600 }}>{fmt(b.total)}</td>
                        <td style={{ padding: "12px 16px" }}>
                          <span className={`badge badge-${b.paid ? "success" : "danger"}`}>{b.paid ? "Paid" : "Unpaid"}</span>
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <span className={`badge badge-${b.status === "confirmed" ? "success" : b.status === "cancelled" ? "danger" : "gold"}`}>{b.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "products" && (
            <div className="fade-in">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
                <h2 style={{ fontSize: 26 }}>Product Management</h2>
                <button className="btn-primary" onClick={() => setTab("add")}>+ Add New Product</button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {allProducts.map(p => (
                  <div key={`${p._type}-${p.id}`} style={{ background: "#fff", borderRadius: 10, border: `1px solid ${C.sand}`, padding: "16px 20px", display: "flex", alignItems: "center", gap: 16 }}>
                    <img src={p.image} alt="" style={{ width: 60, height: 50, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <span style={{ fontWeight: 600, fontSize: 15 }}>{p.title || p.name}</span>
                        <span className={`badge badge-${p.active ? "success" : "danger"}`}>{p.active ? "Active" : "Inactive"}</span>
                        <span className="badge badge-ocean">{p._type}</span>
                        <span className="badge" style={{ background: C.sand, color: C.slate }}>{p.destination}</span>
                      </div>
                      <div style={{ fontSize: 13, color: C.mist, marginTop: 4 }}>
                        {fmt(p.price)} · {p.reviews} reviews · {p.booked || 0} booked
                      </div>
                    </div>
                    <button onClick={() => toggleActive(p)} style={{ padding: "6px 14px", borderRadius: 4, fontSize: 12, border: `1px solid ${C.sandDark}`, background: "#fff", cursor: "pointer" }}>
                      {p.active ? "Deactivate" : "Activate"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "reviews" && (
            <div className="fade-in">
              <h2 style={{ fontSize: 26, marginBottom: 28 }}>Customer Reviews</h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {reviews.map(r => (
                  <div key={r.id} style={{ background: "#fff", borderRadius: 12, border: `1px solid ${C.sand}`, padding: 24 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                      <div>
                        <span style={{ fontWeight: 600 }}>{r.customer}</span>
                        <span style={{ marginLeft: 12, color: C.gold, fontSize: 14 }}>{stars(r.rating)}</span>
                        <span style={{ marginLeft: 8, fontSize: 12, color: C.mist }}>{r.date}</span>
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        {!r.approved && (
                          <button onClick={() => approveReview(r.id)} style={{ padding: "5px 14px", borderRadius: 4, fontSize: 12, background: C.success, color: "#fff", border: "none", cursor: "pointer" }}>Approve</button>
                        )}
                        <button onClick={() => deleteReview(r.id)} style={{ padding: "5px 14px", borderRadius: 4, fontSize: 12, background: C.danger, color: "#fff", border: "none", cursor: "pointer" }}>Delete</button>
                      </div>
                    </div>
                    <div style={{ fontSize: 13, color: C.mist, marginBottom: 10 }}>Re: {r.productTitle}</div>
                    <p style={{ color: C.charcoal, lineHeight: 1.7 }}>{r.comment}</p>
                    <div style={{ marginTop: 10 }}>
                      <span className={`badge badge-${r.approved ? "success" : "gold"}`}>{r.approved ? "Published" : "Pending Review"}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "add" && (
            <AddProductForm onSave={async (product) => {
              try {
                const endpoint = `${API}/${product.category}s`;
                const res = await axios.post(endpoint, product);
                if (product.category === "package") setPackages(p => [...p, res.data]);
                else if (product.category === "excursion") setExcursions(p => [...p, res.data]);
                else setStays(p => [...p, res.data]);
                setTab("products");
              } catch (err) {
                console.error("Failed to save product", err);
              }
            }} />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Add Product Form ─────────────────────────────────────────────────────────
function AddProductForm({ onSave }) {
  const [form, setForm] = useState({ title: "", category: "package", destination: "Zanzibar", price: "", duration: "", description: "", image: "", featured: false, tags: "", includes: "" });
  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const inputStyle = { width: "100%", padding: "10px 14px", border: `1px solid ${C.sandDark}`, borderRadius: 6, fontSize: 14 };

  return (
    <div className="fade-in">
      <h2 style={{ fontSize: 26, marginBottom: 28 }}>Add New Product</h2>
      <div style={{ background: "#fff", borderRadius: 12, border: `1px solid ${C.sand}`, padding: 32, maxWidth: 720 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <div><label style={{ fontSize: 12, color: C.mist, display: "block", marginBottom: 4 }}>Product Title *</label>
            <input name="title" value={form.title} onChange={handleChange} placeholder="e.g. Zanzibar Sunset Cruise" style={inputStyle} /></div>
          <div><label style={{ fontSize: 12, color: C.mist, display: "block", marginBottom: 4 }}>Category *</label>
            <select name="category" value={form.category} onChange={handleChange} style={inputStyle}>
              <option value="package">Travel Package</option>
              <option value="excursion">Excursion / Activity</option>
              <option value="stay">Stay</option>
            </select></div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
          <div><label style={{ fontSize: 12, color: C.mist, display: "block", marginBottom: 4 }}>Destination *</label>
            <select name="destination" value={form.destination} onChange={handleChange} style={inputStyle}>
              {["Zanzibar", "Seychelles", "Madagascar"].map(d => <option key={d}>{d}</option>)}
            </select></div>
          <div><label style={{ fontSize: 12, color: C.mist, display: "block", marginBottom: 4 }}>Price (USD) *</label>
            <input name="price" type="number" value={form.price} onChange={handleChange} placeholder="0" style={inputStyle} /></div>
          <div><label style={{ fontSize: 12, color: C.mist, display: "block", marginBottom: 4 }}>Duration</label>
            <input name="duration" value={form.duration} onChange={handleChange} placeholder="e.g. 7 nights / 8 days" style={inputStyle} /></div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: C.mist, display: "block", marginBottom: 4 }}>Image URL</label>
          <input name="image" value={form.image} onChange={handleChange} placeholder="https://..." style={inputStyle} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: C.mist, display: "block", marginBottom: 4 }}>Description</label>
          <textarea name="description" value={form.description} onChange={handleChange} rows={4} placeholder="Describe this product..." style={{ ...inputStyle, resize: "vertical" }} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <div><label style={{ fontSize: 12, color: C.mist, display: "block", marginBottom: 4 }}>Tags (comma separated)</label>
            <input name="tags" value={form.tags} onChange={handleChange} placeholder="Beach, Culture, Wildlife" style={inputStyle} /></div>
          <div><label style={{ fontSize: 12, color: C.mist, display: "block", marginBottom: 4 }}>What's Included (comma separated)</label>
            <input name="includes" value={form.includes} onChange={handleChange} placeholder="Airport transfer, Breakfast..." style={inputStyle} /></div>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24, cursor: "pointer" }}>
          <input type="checkbox" name="featured" checked={form.featured} onChange={handleChange} />
          <span style={{ fontSize: 14 }}>Mark as Featured Product</span>
        </label>
        <div style={{ display: "flex", gap: 12 }}>
          <button className="btn-primary" onClick={() => {
            if (form.title && form.price && form.destination) {
              onSave({
                ...form,
                price: parseFloat(form.price),
                tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
                includes: form.includes.split(",").map(t => t.trim()).filter(Boolean),
                image: form.image || "https://images.unsplash.com/photo-1590523277543-a94d2e4eb00b?w=800&q=80",
                type: form.category, currency: "USD",
              });
            }
          }}>Save Product</button>
          <button className="btn-outline">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Footer ───────────────────────────────────────────────────────────────────
function Footer({ setPage }) {
  const [destinations, setDestinations] = React.useState([]);

  React.useEffect(() => {
    fetch(`${API}/countries?active=1`)
      .then(res => res.json())
      .then(data => {
        const links = data.map(c => c.name); // ✅ names from DB
        setDestinations(links);
      })
      .catch(err => console.error("Fetch destinations failed:", err));
  }, []);

  const cols = [
    { title: "Destinations", links: destinations },
    { title: "Services", links: ["Travel Packages", "Excursions", "Stays", "Contact"] },
    { title: "Company", links: ["About Us", "Terms & Conditions", "Cancellation Policy", "Privacy Policy"] },
  ];

  return (
    <footer style={{ background: C.charcoal, color: "rgba(255,255,255,0.7)", padding: "56px 5% 32px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 48, marginBottom: 48 }}>
          <div>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 700, color: "#fff", marginBottom: 4 }}>
              Madagascar Holidays Dreams
            </div>
            <div style={{ fontSize: 10, letterSpacing: 3, color: C.goldLight, marginBottom: 16 }}>TRAVEL CO.</div>
            <p style={{ lineHeight: 1.8, fontSize: 14 }}>
              Your trusted partner for extraordinary Indian Ocean island experiences. Based in Zanzibar, experts in the region since 2009.
            </p>
          </div>
          {cols.map(col => (
            <div key={col.title}>
              <h4 style={{ color: "#fff", fontSize: 13, fontWeight: 600, letterSpacing: 2, marginBottom: 16 }}>
                {col.title.toUpperCase()}
              </h4>
              {col.links.map(l => (
                <div
                  key={l}
                  style={{ marginBottom: 10, cursor: "pointer", fontSize: 14, transition: "color 0.2s" }}
                  onClick={() => setPage(slugify(l))}
                  onMouseEnter={e => e.currentTarget.style.color = C.goldLight}
                  onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.7)"}
                >
                  {l}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: 24, display: "flex", justifyContent: "space-between", fontSize: 13 }}>
          <span>© 2025 Madagascar Holidays Dreams. All rights reserved.</span>
          <span>Made with care in Zanzibar</span>
        </div>
      </div>
    </footer>
  );
}


// ── App Root ─────────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState("home");
  const [isAdmin, setIsAdmin] = useState(false);
  const [bookings, setBookings] = useState([]);

  const isAdminPage = page === "admin-login" || (page === "admin" && isAdmin);

  const renderPage = () => {
    if (page === "admin-login") return <AdminLogin onLogin={() => { setIsAdmin(true); setPage("admin"); }} />;
    if (page === "admin" && isAdmin) return <AdminDashboard onLogout={() => { setIsAdmin(false); setPage("home"); }} />;
    if (page === "destinations") return <DestinationsPage setPage={setPage} />;
    if (page === "packages") return <PackagesPage setBookings={setBookings} />;
    if (page === "excursions") return <ExcursionsPage setBookings={setBookings} />;
    if (page === "stays") return <StaysPage setBookings={setBookings} />;
    if (page === "contact") return <ContactPage />;
    return <HomePage setPage={setPage} />;
  };

  return (
    <div>
      <GlobalStyle />
      {!isAdminPage && <Navbar page={page} setPage={setPage} isAdmin={isAdmin} />}
      {renderPage()}
      {!isAdminPage && page !== "admin-login" && (
        <>
          <Footer setPage={setPage} />
          <WhatsAppButton />
        </>
      )}
    </div>
  );
}